import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import TableBox from '../../../components/TableBox';
import StatusBadge from '../../../components/StatusBadge';
import RowActions from '../../../components/RowActions';
import Button from '../../../components/Button';
import Dropdown from '../../../components/Dropdown';
import useFetch from '../../../hooks/useFetch';
import SubscriptionService from '../../../services/subscription.service';
import ProductService from '../../../services/product.service';
import MasterService from '../../../services/master.service';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../enums';
import { sortData } from '../../../utils/sortUtils';

interface SubscriptionRow {
  id: number;
  subscription_no: string;
  tenant_name: string | null;
  product_code: string | null;
  plan_name: string | null;
  start_date: string;
  end_date: string;
  amount: string;
  currency_code: string | null;
  currency_symbol: string | null;
  subscription_status_name: string | null;
  subscription_status_colour: string | null;
  payment_status_name: string | null;
  payment_status_colour: string | null;
  pendingPlanChange: { newPlanName: string; effectiveDate: string } | null;
}

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

interface StatusOption {
  id: number;
  status_name: string;
  colour_code: string;
}

const SubscriptionList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState<SubscriptionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [sortField, setSortField] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.desc);
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expiryFrom, setExpiryFrom] = useState('');
  const [expiryTo, setExpiryTo] = useState('');
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
    MasterService.list({ type: 'subscriptionStatuses', includeDeleted: false, limit: 100 }).then((response: any) => {
      setStatuses(response?.data?.items ?? []);
    });
  }, []);

  const { loading } = useFetch({
    service: SubscriptionService,
    func: 'list',
    payload: {
      page: pageNo,
      limit: rowsPerPage,
      orderBy: sortField,
      order: sortOrder,
      productId: productFilter ? Number(productFilter) : undefined,
      status: statusFilter || undefined,
      expiryFrom: expiryFrom || undefined,
      expiryTo: expiryTo || undefined,
    },
    dependency: [pageNo, rowsPerPage, sortField, sortOrder, productFilter, statusFilter, expiryFrom, expiryTo],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'subscription_no', sortEnabled: true, width: '12%', label: t('subscriptions.list.pageTexts.table.columns.subscriptionNo') },
      { id: 'tenant_name', sortEnabled: true, width: '16%', label: t('subscriptions.list.pageTexts.table.columns.tenant') },
      { id: 'product_code', sortEnabled: true, width: '10%', label: t('subscriptions.list.pageTexts.table.columns.product') },
      { id: 'plan_name', sortEnabled: true, width: '13%', label: t('subscriptions.list.pageTexts.table.columns.plan') },
      { id: 'end_date', sortEnabled: true, width: '11%', label: t('subscriptions.list.pageTexts.table.columns.endDate') },
      { id: 'amount', sortEnabled: true, width: '10%', label: t('subscriptions.list.pageTexts.table.columns.amount') },
      { id: 'subscription_status_name', sortEnabled: true, width: '13%', label: t('subscriptions.list.pageTexts.table.columns.status') },
      { id: 'payment_status_name', sortEnabled: true, width: '13%', label: t('subscriptions.list.pageTexts.table.columns.paymentStatus') },
      { id: 'actions', sortEnabled: false, width: '12%', label: t('subscriptions.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const Row = useCallback(
    ({ item, className }: { item: SubscriptionRow; className?: string }) => (
      <tr className={className}>
        <td>{item.subscription_no}</td>
        <td>{item.tenant_name ?? '—'}</td>
        <td>{item.product_code ?? '—'}</td>
        <td>
          <div className="d-flex align-items-center gap-2">
            <span>{item.plan_name ?? '—'}</span>
            {item.pendingPlanChange && (
              <Tooltip
                title={t('subscriptions.list.pageTexts.pendingPlanChangeTooltip', {
                  currentPlan: item.plan_name,
                  newPlan: item.pendingPlanChange.newPlanName,
                  effectiveDate: item.pendingPlanChange.effectiveDate,
                })}
              >
                <Chip size="small" color="info" label={t('subscriptions.list.pageTexts.pendingPlanChangeChip')} />
              </Tooltip>
            )}
          </div>
        </td>
        <td>{String(item.end_date).slice(0, 10)}</td>
        <td>
          {item.currency_symbol ?? ''}
          {item.amount}
        </td>
        <td>
          {item.subscription_status_name && item.subscription_status_colour ? (
            <StatusBadge label={item.subscription_status_name} colour={item.subscription_status_colour} />
          ) : (
            '—'
          )}
        </td>
        <td>
          {item.payment_status_name && item.payment_status_colour ? (
            <StatusBadge label={item.payment_status_name} colour={item.payment_status_colour} />
          ) : (
            '—'
          )}
        </td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'edit',
                label: t('common.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => navigate(`${Modules.home}${AuthRoutes.subscriptions}${RouteParamsTypes.edit}/${item.id}`),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [navigate]
  );

  const sortedItems = useMemo(() => sortData(items, sortField, sortOrder), [items, sortField, sortOrder]);

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order: sortOrder,
      orderBy: sortField,
      page: pageNo,
      perPage: rowsPerPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('subscriptions.list.pageTexts.table.noDataFoundMessage'),
      setPage: setPageNo,
      setPerPage: setRowsPerPage,
      setOrder: setSortOrder,
      setOrderBy: setSortField,
    }),
    [headCells, Row, total, loading, sortedItems, pageNo, rowsPerPage, sortField, sortOrder, t]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('subscriptions.list.pageTexts.title')}</h4>
        <Button
          className="btn btn-primary"
          text={t('subscriptions.list.pageTexts.addNew')}
          onClick={() => navigate(`${Modules.home}${AuthRoutes.subscriptions}${RouteParamsTypes.add}`)}
        />
      </div>

      <div className="row mb-4 g-2">
        <div className="col-md-3">
          <Dropdown
            name="productFilter"
            value={productFilter}
            placeholder={t('subscriptions.list.pageTexts.filters.allProducts')}
            options={products.map((p) => ({ id: String(p.id), name: p.product_code }))}
            noSearch
            onChange={(e) => setProductFilter(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <Dropdown
            name="statusFilter"
            value={statusFilter}
            placeholder={t('subscriptions.list.pageTexts.filters.allStatuses')}
            options={statuses.map((s) => ({ id: String(s.id), name: s.status_name }))}
            noSearch
            onChange={(e) => setStatusFilter(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <input
            type="date"
            className="form-control"
            aria-label={t('subscriptions.list.pageTexts.filters.expiryFrom')}
            value={expiryFrom}
            onChange={(e) => setExpiryFrom(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <input
            type="date"
            className="form-control"
            aria-label={t('subscriptions.list.pageTexts.filters.expiryTo')}
            value={expiryTo}
            onChange={(e) => setExpiryTo(e.target.value)}
          />
        </div>
      </div>

      <TableBox tableValues={values} />
    </>
  );
};

export default SubscriptionList;
